/**
 * EventEditPage - Mobile-First Event Editing
 * 
 * Enhanced event editing form with mobile-optimized UX,
 * sectioned design, and destructive action handling.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7E
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvent, useUpdateEvent, useDeleteEvent, useUploadEventHeroImage, useRemoveEventHeroImage } from '../hooks/useEvents';
import { TopBar, Button, EmptyState, DurationInput, DistanceInput, HeroImageUpload, Skeleton } from '../components';
import EventVisibilityToggle from '../components/EventVisibilityToggle';
import { Calendar, MapPin, Clock, Users, AlertTriangle, Trash2, Image as ImageIcon } from 'lucide-react';
import type { ApiError } from '../lib/api';

interface FormData {
  title: string;
  description: string;
  starts_at: string;
  duration_minutes: number; // Duration in minutes instead of end time
  distance_km: number; // Optional distance in kilometers
  location_name: string;
  location_address: string;
  visibility: 'public' | 'members_only';
  check_ins_enabled: boolean;
  checkin_radius_m: number;
  checkin_window_before_min: number;
  checkin_window_after_min: number;
}

export default function EventEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Fetch event data
  const { data: eventData, isLoading, error: fetchError } = useEvent(id || '');
  const event = eventData;
  const clubId = event?.club_id || '';
  
  // Mutations
  const updateEvent = useUpdateEvent(id || '', clubId);
  const deleteEvent = useDeleteEvent(clubId);
  const uploadHeroImage = useUploadEventHeroImage(id || '', clubId);
  const removeHeroImage = useRemoveEventHeroImage(id || '', clubId);
  
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    starts_at: '',
    duration_minutes: 120, // Default to 2 hours
    distance_km: 0, // Default to no distance
    location_name: '',
    location_address: '',
    visibility: 'public',
    check_ins_enabled: true,
    checkin_radius_m: 150,
    checkin_window_before_min: 30,
    checkin_window_after_min: 30,
  });
  
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Helper to convert Unix timestamp (seconds) to datetime-local format
  const timestampToDatetimeLocal = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Calculate end time from start time and duration (matching EventCreatePage pattern)
  const calculateEndTime = (startTime: string, durationMinutes: number): Date | null => {
    if (!startTime || durationMinutes <= 0) return null;
    
    const startDate = new Date(startTime);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    return endDate;
  };



  // Populate form when event data loads
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (event) {
      // Calculate duration from existing start and end times
      let durationMinutes = 120; // Default 2 hours
      if (event.starts_at && event.ends_at) {
        durationMinutes = Math.floor((event.ends_at - event.starts_at) / 60);
      }
      
      setFormData({
        title: event.title,
        description: event.description || '',
        starts_at: timestampToDatetimeLocal(event.starts_at),
        duration_minutes: durationMinutes,
        distance_km: event.distance_km || 0,
        location_name: event.location_name || '',
        location_address: event.location_address || '',
        visibility: (event.visibility as 'public' | 'members_only') || 'public',
        check_ins_enabled: Boolean(event.check_ins_enabled ?? 1),
        checkin_radius_m: event.checkin_radius_m ?? 150,
        checkin_window_before_min: event.checkin_window_before_min ?? 30,
        checkin_window_after_min: event.checkin_window_after_min ?? 30,
      });
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [event]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!id) return;
    
    // Enhanced validation
    if (!formData.title.trim()) {
      setValidationError('Event title is required');
      return;
    }
    
    if (!formData.starts_at) {
      setValidationError('Start date/time is required');
      return;
    }
    
    if (formData.duration_minutes < 15) {
      setValidationError('Event duration must be at least 15 minutes');
      return;
    }
    
    if (formData.duration_minutes > 24 * 60) {
      setValidationError('Event duration cannot exceed 24 hours');
      return;
    }
    
    // Validate start time
    const startsAt = new Date(formData.starts_at);
    const now = new Date();
    
    if (startsAt <= now) {
      setValidationError('Event must start in the future');
      return;
    }
    
    setValidationError(null);
    
    // Calculate end time from start time and duration
    const endsAt = new Date(startsAt.getTime() + formData.duration_minutes * 60 * 1000);
    
    // Client-side validation: force members_only for locked clubs (Issue #427)
    const clubVisibility = event?.club_visibility || 'public';
    const isLockedClub = clubVisibility === 'private' || clubVisibility === 'invite_only';
    const finalVisibility = isLockedClub ? 'members_only' : formData.visibility;
    
    // Convert form data to API request format
    const updateRequest = {
      title: formData.title,
      description: formData.description || undefined,
      starts_at: Math.floor(new Date(formData.starts_at).getTime() / 1000),
      ends_at: Math.floor(endsAt.getTime() / 1000),
      location_name: formData.location_name || undefined,
      location_address: formData.location_address || undefined,
      visibility: finalVisibility,
      distance_km: formData.distance_km > 0 ? formData.distance_km : undefined,
      check_ins_enabled: formData.check_ins_enabled,
      checkin_radius_m: formData.checkin_radius_m,
      checkin_window_before_min: formData.checkin_window_before_min,
      checkin_window_after_min: formData.checkin_window_after_min,
    };
    
    // Update event using mutation
    updateEvent.mutate(updateRequest, {
      onSuccess: () => {
        navigate(`/event/${id}`);
      },
    });
  };

  const handleDelete = async () => {
    if (!id || !event?.club_slug) return;
    
    deleteEvent.mutate(id, {
      onSuccess: () => {
        navigate(`/clubs/${event.club_slug}/events`);
      },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Edit Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-6 space-y-6">
            {/* Hero image skeleton */}
            <Skeleton variant="image" className="h-48 w-full rounded-2xl" />
            
            {/* Form fields skeleton */}
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            ))}
            
            {/* Save button skeleton */}
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError || !event || !id) {
    const errorMessage = fetchError 
      ? 'Failed to load event'
      : !event 
      ? 'Event not found'
      : 'Invalid event ID';
      
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Edit Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              icon="📅"
              title="Unable to edit event"
              message={errorMessage}
              actionLabel='Go Back' onAction={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Delete confirmation modal
  if (showDeleteConfirm) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Delete Event" onBack={() => setShowDeleteConfirm(false)} />
        <div className="flex-1 overflow-y-auto flex items-center justify-center px-4">
          <div className="bg-oro-surface border border-red-500/20 rounded-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="font-semibold text-text-primary">Delete Event</h2>
                <p className="text-text-muted text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-text-secondary text-sm mb-6">
              Are you sure you want to delete "<strong>{event.title}</strong>"? 
              All event data and check-ins will be permanently removed.
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={handleDelete}
                variant="primary"
                className="w-full bg-red-500 hover:bg-red-600 border-red-500"
                disabled={deleteEvent.isPending}
              >
                {deleteEvent.isPending ? 'Deleting...' : 'Delete Event'}
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="secondary"
                className="w-full"
                disabled={deleteEvent.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title="Edit Event" 
        onBack={() => navigate(`/event/${id}`)} 
      />
      
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} className="px-4 py-6 space-y-6">
          {/* Event Context */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-text-muted" />
              <span className="text-text-secondary text-sm">Editing event</span>
            </div>
            <h2 className="font-semibold text-text-primary">{event.title}</h2>
            <p className="text-text-muted text-xs mt-1">
              Created {new Date(event.created_at * 1000).toLocaleDateString()}
            </p>
          </div>

          {/* Event Details */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-text-primary">Event Details</h3>
            
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-text-secondary mb-2">
                Event Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Morning Trail Run"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                rows={3}
                placeholder="Join us for an energizing trail run..."
              />
            </div>
          </div>

          {/* Hero Image Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-text-muted" />
              <h3 className="font-semibold text-text-primary">Event Photo</h3>
            </div>
            
            <p className="text-sm text-text-secondary">
              Upload a photo for this event. If no photo is uploaded, your club's photo will be used.
            </p>
            
            <HeroImageUpload
              currentImageUrl={event?.hero_image_url}
              onImageChange={() => {
                // Image change handled by upload/remove callbacks
                // No local state needed since we refetch from server
              }}
              onImageUpload={async (file) => {
                const response = await uploadHeroImage.mutateAsync(file);
                return response.image_url;
              }}
              onImageRemove={async () => {
                await removeHeroImage.mutateAsync();
              }}
              enableCrop={true}
              className="w-full"
              disabled={uploadHeroImage.isPending || removeHeroImage.isPending || updateEvent.isPending}
            />
            
            {(uploadHeroImage.error || removeHeroImage.error) && (
              <div className="text-red-500 text-sm">
                {uploadHeroImage.error?.message || removeHeroImage.error?.message}
              </div>
            )}
          </div>

          {/* Event Visibility (Issue #427) */}
          <EventVisibilityToggle
            value={formData.visibility}
            onChange={(visibility) => setFormData({ ...formData, visibility })}
            clubVisibility={event?.club_visibility as 'public' | 'private' | 'invite_only'}
          />

          {/* DateTime Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-text-muted" />
              <h3 className="font-semibold text-text-primary">Date & Time</h3>
            </div>
            
            <div>
              <label htmlFor="starts_at" className="block text-sm font-medium text-text-secondary mb-2">
                Starts At *
              </label>
              <input
                type="datetime-local"
                id="starts_at"
                value={formData.starts_at}
                onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <DurationInput
                label="Duration *"
                value={formData.duration_minutes}
                onChange={(minutes) => setFormData({ ...formData, duration_minutes: minutes })}
                placeholder="e.g. 2h, 1h 30m"
                minMinutes={15}
                maxMinutes={24 * 60}
              />
            </div>

            {/* Calculated End Time Display */}
            {formData.starts_at && formData.duration_minutes > 0 && (() => {
              const calculatedEndTime = calculateEndTime(formData.starts_at, formData.duration_minutes);
              if (!calculatedEndTime) return null;
              
              return (
                <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-text-muted" />
                    <span className="text-text-secondary text-sm">
                      Event ends at: {calculatedEndTime.toLocaleString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Distance Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="font-semibold text-text-primary">Distance</h3>
              <span className="text-xs text-text-muted">(Optional)</span>
            </div>
            
            <DistanceInput
              label=""
              value={formData.distance_km}
              onChange={(distanceKm) => setFormData({ ...formData, distance_km: distanceKm })}
              placeholder="e.g. 5.2"
            />
          </div>

          {/* Location Section */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-text-muted" />
              <h3 className="font-semibold text-text-primary">Location</h3>
            </div>
            
            <div>
              <label htmlFor="location_name" className="block text-sm font-medium text-text-secondary mb-2">
                Location Name
              </label>
              <input
                type="text"
                id="location_name"
                value={formData.location_name}
                onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Central Park Entrance"
              />
            </div>

            <div>
              <label htmlFor="location_address" className="block text-sm font-medium text-text-secondary mb-2">
                Full Address <span className="text-text-muted">(optional)</span>
              </label>
              <input
                type="text"
                id="location_address"
                value={formData.location_address}
                onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Central Park, New York, NY 10024"
              />
            </div>
          </div>

          {/* Check-in Settings */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-text-primary">Check-in Settings</h3>
            
            {/* Check-ins Toggle */}
            <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.check_ins_enabled}
                  onChange={(e) => setFormData({ ...formData, check_ins_enabled: e.target.checked })}
                  className="w-5 h-5 text-accent bg-oro-surface border-oro-border rounded mt-0.5 focus:ring-accent focus:ring-2"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-text-primary">Enable check-ins</span>
                  <p className="text-xs text-text-muted mt-1">
                    Allow members to check in when they arrive at the event
                  </p>
                </div>
              </label>
            </div>
            
            {/* Check-in Parameters - Collapsible */}
            {formData.check_ins_enabled && (
              <div className="space-y-4 border-t border-oro-border pt-4">
                <div>
                  <label htmlFor="checkin_radius_m" className="block text-sm font-medium text-text-secondary mb-2">
                    Check-in Radius (meters)
                  </label>
                  <select
                    id="checkin_radius_m"
                    value={formData.checkin_radius_m}
                    onChange={(e) => setFormData({ ...formData, checkin_radius_m: parseInt(e.target.value, 10) })}
                    className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value={50}>50m - Indoor venue</option>
                    <option value={100}>100m - Small outdoor area</option>
                    <option value={150}>150m - Default</option>
                    <option value={300}>300m - Large park/area</option>
                    <option value={500}>500m - Very large area</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="checkin_window_before_min" className="block text-sm font-medium text-text-secondary mb-2">
                      Before (min)
                    </label>
                    <select
                      id="checkin_window_before_min"
                      value={formData.checkin_window_before_min}
                      onChange={(e) => setFormData({ ...formData, checkin_window_before_min: parseInt(e.target.value, 10) })}
                      className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="checkin_window_after_min" className="block text-sm font-medium text-text-secondary mb-2">
                      After (min)
                    </label>
                    <select
                      id="checkin_window_after_min"
                      value={formData.checkin_window_after_min}
                      onChange={(e) => setFormData({ ...formData, checkin_window_after_min: parseInt(e.target.value, 10) })}
                      className="w-full bg-oro-surface-alt border border-oro-border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="space-y-3 pb-4">
            {/* Error Display - shown near submit button for better UX */}
            {(validationError || updateEvent.error) && (
              <div className="bg-oro-error-soft border border-oro-error-border rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-oro-error shrink-0 mt-0.5" />
                <p className="text-oro-error text-sm">
                  {validationError || ((updateEvent.error as unknown as ApiError)?.message || 'Failed to update event')}
                </p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={updateEvent.isPending}
            >
              {updateEvent.isPending ? 'Saving Changes...' : 'Save Changes'}
            </Button>
            
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => navigate(`/event/${id}`)}
              disabled={updateEvent.isPending}
            >
              Cancel
            </Button>
          </div>

          {/* Danger Zone */}
          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Trash2 className="w-4 h-4 text-red-400" />
              <h3 className="font-semibold text-red-400">Danger Zone</h3>
            </div>
            <p className="text-text-muted text-sm mb-4">
              Deleting this event will permanently remove all event data and check-ins. This action cannot be undone.
            </p>
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              variant="secondary"
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
              disabled={updateEvent.isPending}
            >
              Delete Event
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
